import React, { useCallback, useEffect, useId, useMemo, useRef, useState, } from 'react';
import clsx from 'clsx';
import Close from '@mollie/ui-assets/icons/utilitarian/Close';
import { suffixId, useClickOutside, useComposedRefs, useControlledValue, useEscKeyHandler, useFocusScope, useFocusTrap, useScrollLock, safeFocus, useAnimation, DeferredPromise, useResponsiveProp, usePrevious, } from '../../utils';
import { Icon } from '../Icon';
import { Portal } from '../Portal';
import { Text } from '../Text';
import { ModalContext } from './ModalContext';
import { ModalCloseButton } from './ModalCloseButton';
import { Box } from '../Box';
import { TextContainer } from '../TextContainer';
import { modalStoreApi, useModalStore } from '../ModalStore';
import './Modal.css';
export function Modal(props) {
    const { children, id: userId, trigger, title, headerAction, complement, footer, open: userOpen, defaultOpen = false, disableClickOutside = false, disableScrollLock = false, disableEscKey = false, ariaCloseLabel = 'Close modal', onClose: userOnClose, onOpen, onChangeOpen, onAfterClose, size = 'large', className, UNSAFE_wrapperClassName, asyncApiRef, elementType = 'section', imageSrc, imageAlt, imageDark, variant, dialog = variant === 'dialog', UNSAFE_keepOpenOnQueue, theme, ...rest } = props;
    const promiseRef = useRef();
    const onChange = useCallback((open) => {
        if (open) {
            onOpen === null || onOpen === void 0 ? void 0 : onOpen();
        }
        else {
            userOnClose === null || userOnClose === void 0 ? void 0 : userOnClose();
        }
        onChangeOpen === null || onChangeOpen === void 0 ? void 0 : onChangeOpen(open);
    }, [userOnClose, onOpen, onChangeOpen]);
    const [open, setOpen] = useControlledValue({
        value: userOpen,
        onChange,
        defaultValue: defaultOpen,
    });
    const [modalElement, setModalElement] = useState(null);
    const modalRef = useRef(null);
    const activeElementRef = useRef(null);
    const composedModalRef = useComposedRefs(modalRef, setModalElement);
    const focusScope = useFocusScope(modalRef);
    useFocusTrap(focusScope);
    const uniqueId = useId();
    const id = userId !== null && userId !== void 0 ? userId : uniqueId;
    const titleId = suffixId(id, 'title');
    const modalStore = useModalStore();
    const activeModalId = getLastValue(modalStore.queue);
    const isModalInForeground = activeModalId === id;
    const isModalInBackground = modalStore.queue.has(id) && id !== activeModalId;
    const wasPreviouslyOnBackground = usePrevious(isModalInBackground);
    const onTrigger = useCallback(() => {
        setOpen(true);
    }, [setOpen]);
    const onClose = useCallback(() => {
        if (!isModalInBackground) {
            setOpen(false);
        }
    }, [isModalInBackground, setOpen]);
    useClickOutside(modalElement, () => {
        var _a;
        if (!dialog && !disableClickOutside) {
            return onClose();
        }
        if (isModalInForeground) {
            return (_a = modalRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
    });
    useEscKeyHandler(onClose, (e) => {
        var _a;
        return disableEscKey
            ? false
            : Boolean((_a = modalRef.current) === null || _a === void 0 ? void 0 : _a.contains(e.target));
    });
    useScrollLock(!disableScrollLock && open);
    useEffect(() => {
        let timeout;
        if (open) {
            activeElementRef.current = document.activeElement;
            timeout = window.setTimeout(() => {
                focusScope.autofocus();
            }, 0);
        }
        else {
            activeElementRef.current = null;
        }
        return () => {
            clearTimeout(timeout);
            safeFocus(activeElementRef.current);
        };
    }, [focusScope, open]);
    const { canMount, animationClassNames } = useAnimation(modalRef, open && !(isModalInBackground && !UNSAFE_keepOpenOnQueue), {
        onAfterAnimationEnded: () => {
            var _a;
            if (!open) {
                onAfterClose === null || onAfterClose === void 0 ? void 0 : onAfterClose();
            }
            (_a = promiseRef.current) === null || _a === void 0 ? void 0 : _a.resolve(open);
        },
        canUnmount: !isModalInBackground,
    });
    const triggerElement = typeof trigger === 'string' ? (React.createElement("span", null, trigger)) : trigger ? (React.Children.only(trigger)) : null;
    const asyncApi = useMemo(() => ({
        toggleOpen: () => {
            promiseRef.current = new DeferredPromise();
            setOpen((open) => !open);
            return promiseRef.current.promise;
        },
        open: () => {
            promiseRef.current = new DeferredPromise();
            setOpen(true);
            return promiseRef.current.promise;
        },
        close: () => {
            promiseRef.current = new DeferredPromise();
            setOpen(false);
            return promiseRef.current.promise;
        },
    }), [setOpen]);
    useEffect(() => {
        if (open) {
            modalStoreApi.queue(id);
        }
        else {
            modalStoreApi.dequeue(id);
        }
        return () => {
            modalStoreApi.dequeue(id);
        };
    }, [id, open]);
    useEffect(() => {
        // clean up after Modal has been removed from the queue and was previously on the background
        if (wasPreviouslyOnBackground && !modalStore.queue.has(id)) {
            setOpen(false);
        }
    }, [wasPreviouslyOnBackground, id, modalStore.queue, setOpen]);
    if (asyncApiRef) {
        asyncApiRef.current = asyncApi;
    }
    const responsiveImageSrc = useResponsiveProp(imageSrc);
    return (React.createElement(ModalContext.Provider, { value: useMemo(() => ({ initialized: true, onClose, onTrigger }), [onClose, onTrigger]) },
        triggerElement &&
            React.cloneElement(triggerElement, {
                onClick: () => {
                    onTrigger();
                },
                ...triggerElement.props,
            }),
        canMount && (React.createElement(Portal, { key: modalStore.queue.size, theme: theme },
            React.createElement(Box.Restricted, { ...rest, elementType: elementType, className: clsx('mollie-ui-modal-wrapper', {
                    [`mollie-ui-modal-wrapper--side-panel`]: variant === 'side-panel',
                }, animationClassNames, UNSAFE_wrapperClassName) },
                React.createElement("section", { className: clsx('mollie-ui-modal', {
                        [`mollie-ui-modal--${size}`]: size,
                        [`mollie-ui-modal--dialog`]: dialog,
                        [`mollie-ui-modal--side-panel`]: variant === 'side-panel',
                    }, animationClassNames, className), role: "dialog", "aria-modal": "true", "aria-labelledby": title ? titleId : undefined, ref: composedModalRef, id: id, tabIndex: -1 },
                    (!dialog || title) && (React.createElement("header", { className: clsx('mollie-ui-modal-header', {
                            'mollie-ui-modal-header--with-action': headerAction,
                            'mollie-ui-modal-header--with-image': responsiveImageSrc,
                        }) },
                        responsiveImageSrc && (React.createElement("img", { src: responsiveImageSrc, alt: imageAlt, className: "mollie-ui-modal-image" })),
                        headerAction && (React.createElement("div", { className: "mollie-ui-modal-action" }, headerAction)),
                        title && (React.createElement(Text, { elementType: "h2", variant: {
                                base: 'heading-title-3',
                                extraSmall: 'heading-title-4',
                            }, className: "mollie-ui-modal-title", id: titleId }, title)),
                        complement && (React.createElement("div", { className: "mollie-ui-modal-complement" }, complement)),
                        !dialog && (React.createElement("div", { className: "mollie-ui-modal-close" },
                            React.createElement(Modal.CloseButton, { variant: "icon", "aria-label": ariaCloseLabel, "data-mollie-ui-theme": responsiveImageSrc && imageDark ? 'dark' : undefined },
                                React.createElement(Icon, { src: Close })))))),
                    React.createElement(TextContainer, { className: "mollie-ui-modal-content" }, children),
                    footer && (React.createElement("footer", { className: "mollie-ui-modal-footer" }, footer))))))));
}
Modal.CloseButton = ModalCloseButton;
export { useModalContext } from './ModalContext';
export { modalStoreApi } from '../ModalStore';
function getLastValue(set) {
    let value;
    for (value of set)
        ;
    return value;
}
//# sourceMappingURL=Modal.js.map